import React, { useState, useCallback } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
} from 'reactflow';
import 'reactflow/dist/style.css';

// Custom node component with editable label
const EditableNode = ({ data, id }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(data.label);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleChange = (e) => {
    setLabel(e.target.value);
  };

  const handleBlur = () => {
    setIsEditing(false);
    data.label = label; // Update the node's data
  };

  return (
    <div
      style={{
        padding: '10px',
        borderRadius: '5px',
        background: data.background || '#fff',
        border: '1px solid #ddd',
        minWidth: '120px',
        textAlign: 'center',
      }}
      onDoubleClick={handleDoubleClick}
    >
      {isEditing ? (
        <input
          type="text"
          value={label}
          onChange={handleChange}
          onBlur={handleBlur}
          autoFocus
          style={{ width: '100%', border: 'none', textAlign: 'center' }}
        />
      ) : (
        <div>{label}</div>
      )}
    </div>
  );
};

// Node types
const nodeTypes = {
  input: ({ data, id }) => (
    <EditableNode
      data={{ ...data, background: '#6ee7b7' }}
      id={id}
    />
  ),
  output: ({ data, id }) => (
    <EditableNode
      data={{ ...data, background: '#ff6b6b', color: 'white' }}
      id={id}
    />
  ),
  default: ({ data, id }) => (
    <EditableNode
      data={data}
      id={id}
    />
  ),
};

// Initial nodes and edges
const initialNodes = [
  {
    id: '1',
    type: 'input',
    data: { label: 'Start' },
    position: { x: 250, y: 5 },
  },
  {
    id: '2',
    type: 'default',
    data: { label: 'Decision' },
    position: { x: 250, y: 100 },
  },
  {
    id: '3',
    type: 'default',
    data: { label: 'Process 1' },
    position: { x: 100, y: 200 },
  },
  {
    id: '4',
    type: 'default',
    data: { label: 'Process 2' },
    position: { x: 400, y: 200 },
  },
  {
    id: '5',
    type: 'output',
    data: { label: 'End' },
    position: { x: 250, y: 300 },
  },
];

const initialEdges = [
  { id: 'e1-2', source: '1', target: '2' },
  { id: 'e2-3', source: '2', target: '3', label: 'Yes' },
  { id: 'e2-4', source: '2', target: '4', label: 'No' },
  { id: 'e3-5', source: '3', target: '5' },
  { id: 'e4-5', source: '4', target: '5' },
];

let id = 0;
const getId = () => `dndnode_${id++}`;

const DnDFlow = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowInstance) return;

      const type = event.dataTransfer.getData('application/reactflow');
      if (typeof type === 'undefined' || !type) return;

      const position = reactFlowInstance.project({
        x: event.clientX,
        y: event.clientY - 40,
      });
      const newNode = {
        id: getId(),
        type,
        position,
        data: { label: `${type === 'input' ? 'Start' : type === 'output' ? 'End' : 'Process'} Node` },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance]
  );

  return (
    <div style={{ height: '100vh', width: '100vw' }}>
      <ReactFlowProvider>
        <div style={{ display: 'flex', height: '100%' }}>
          <div
            style={{
              width: '200px',
              background: '#f0f0f0',
              padding: '10px',
              borderRight: '1px solid #ccc',
            }}
          >
            <h3>Drag Nodes</h3>
            <div
              style={{ padding: '8px', margin: '5px', background: '#fff', border: '1px solid #ddd', cursor: 'grab' }}
              onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'input')}
              draggable
            >
              Start Node
            </div>
            <div
              style={{ padding: '8px', margin: '5px', background: '#fff', border: '1px solid #ddd', cursor: 'grab' }}
              onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'default')}
              draggable
            >
              Process Node
            </div>
            <div
              style={{ padding: '8px', margin: '5px', background: '#fff', border: '1px solid #ddd', cursor: 'grab' }}
              onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'output')}
              draggable
            >
              End Node
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onInit={setReactFlowInstance}
              fitView
              nodeTypes={nodeTypes}
            >
              <Controls />
              <Background />
            </ReactFlow>
          </div>
        </div>
      </ReactFlowProvider>
    </div>
  );
};

export default DnDFlow;